import { useEffect, useRef } from 'react';

import { css } from '@compiled/react';

const VIEWBOX = 44; // icon coordinate space (44x44)
const SQUARE = 2; // square edge in icon units

type Cell = [number, number];

const NUM = (attrs: string, name: string): number | null => {
	const m = new RegExp(`\\b${name}=["'](-?\\d+(?:\\.\\d+)?)["']`).exec(attrs);
	return m?.[1] ? parseFloat(m[1]) : null;
};

function parseCells(svg: string): Cell[] {
	const cells: Cell[] = [];
	const rectRe = /<rect\b([^>]*?)\/?>/g;
	let m: RegExpExecArray | null;
	while ((m = rectRe.exec(svg))) {
		const attrs = m[1] as (typeof m)[1];
		// Only the icon's pixel squares (SQUARE x SQUARE). This skips structural
		// rects such as clipPath/mask bounds (e.g. a full 44x44 rect in <defs>).
		if (NUM(attrs, 'width') !== SQUARE || NUM(attrs, 'height') !== SQUARE) continue;
		cells.push([NUM(attrs, 'x') ?? 0, NUM(attrs, 'y') ?? 0]);
	}
	return cells;
}

function parseColor(svg: string): string {
	const m = /fill="(#[0-9a-fA-F]{3,8})"/.exec(svg);
	return m?.[1] ?? '#4D8CED';
}

type Spring = {
	rx: number; // rest position (CSS px)
	ry: number;
	ox: number; // current offset from rest
	oy: number;
	vx: number; // offset velocity
	vy: number;
};

const canvasCss = css({
	pointerEvents: 'none',
});

export interface UniformPixelIconProps {
	/**
	 * URL of the SVG image to render. The SVG markup is fetched from this URL
	 * and its pixel squares are parsed out — a raw SVG string is NOT accepted.
	 */
	svg: string;
	/**
	 * Pixel size of the square icon. Defaults to 44. Accepts a number or a
	 * numeric string — Uniform passes parameter values as strings, so the
	 * value is coerced to a number internally.
	 */
	size?: number | string;
	color?: string;
}

const DEFAULT_SIZE = 44;

const UniformPixelIcon = ({ svg, size, color }: UniformPixelIconProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	// Coerce `size` to a positive finite number. Uniform delivers parameter
	// values as strings, so a raw `size` could be "88" (or "" / undefined),
	// which would turn every downstream calculation into NaN without this.
	const numericSize = ((): number => {
		const parsed = Number(size);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_SIZE;
	})();

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		let cleanupAnimation: (() => void) | undefined;
		let cancelled = false;

		const setup = (svgMarkup: string) => {
			if (cancelled) return;

			// Use the coerced numeric size for all geometry below.
			const size = numericSize;
			const scale = size / VIEWBOX;
			const sq = SQUARE * scale;
			const half = sq / 2;
			const fill = color ?? parseColor(svgMarkup);
			const cells = parseCells(svgMarkup);
			if (cells.length === 0) {
				return;
			}

			// Repulsion tuned relative to the icon size so it scales with `size`.
			const HOVER_RADIUS = size * 0.62; // reach (px)
			const PUSH_MAX = size * 0.2; // strongest displacement, right at the cursor
			const STIFFNESS = 0.06; // pull toward target offset (floaty)
			const DAMPING = 0.82; // velocity retained each frame (momentum/overshoot)
			const SETTLE = 0.05; // offset/velocity threshold to call it rest

			const springs: Spring[] = cells.map(([x, y]) => ({
				rx: (x + SQUARE / 2) * scale,
				ry: (y + SQUARE / 2) * scale,
				ox: 0,
				oy: 0,
				vx: 0,
				vy: 0,
			}));

			const dpr = Math.max(1, window.devicePixelRatio || 1);
			canvas.width = Math.round(size * dpr);
			canvas.height = Math.round(size * dpr);
			canvas.style.width = `${size}px`;
			canvas.style.height = `${size}px`;
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

			let mouseX = -9999;
			let mouseY = -9999;
			let pointerOver = false;
			let raf = 0;
			let running = false;
			let visible = true;

			function draw() {
				if (!ctx) return;
				ctx.clearRect(0, 0, size, size);
				ctx.fillStyle = fill;
				for (const s of springs) {
					ctx.fillRect(s.rx + s.ox - half, s.ry + s.oy - half, sq, sq);
				}
			}

			function frame() {
				let active = false;
				for (const s of springs) {
					let tx = 0;
					let ty = 0;
					if (pointerOver) {
						const dx = s.rx - mouseX; // points away from the cursor
						const dy = s.ry - mouseY;
						const d = Math.hypot(dx, dy);
						if (d < HOVER_RADIUS && d > 0.001) {
							const f = 1 - d / HOVER_RADIUS;
							const push = PUSH_MAX * f * f; // squared falloff
							const inv = push / d;
							tx = dx * inv;
							ty = dy * inv;
						}
					}
					s.vx = s.vx * DAMPING + (tx - s.ox) * STIFFNESS;
					s.vy = s.vy * DAMPING + (ty - s.oy) * STIFFNESS;
					s.ox += s.vx;
					s.oy += s.vy;
					if (
						Math.abs(s.ox) > SETTLE ||
						Math.abs(s.oy) > SETTLE ||
						Math.abs(s.vx) > SETTLE ||
						Math.abs(s.vy) > SETTLE
					) {
						active = true;
					}
				}

				draw();

				if (visible && (pointerOver || active)) {
					raf = requestAnimationFrame(frame);
				} else {
					running = false;
					// Only collapse to the clean resting icon while on-screen. When
					// hidden we keep the spring state so the loop can resume settling
					// on re-entry.
					if (visible) {
						for (const s of springs) {
							s.ox = 0;
							s.oy = 0;
							s.vx = 0;
							s.vy = 0;
						}
						draw();
					}
				}
			}

			function start() {
				if (!running && visible) {
					running = true;
					raf = requestAnimationFrame(frame);
				}
			}

			const springsActive = () =>
				springs.some(
					(s) =>
						Math.abs(s.ox) > SETTLE ||
						Math.abs(s.oy) > SETTLE ||
						Math.abs(s.vx) > SETTLE ||
						Math.abs(s.vy) > SETTLE,
				);

			const target = canvas.parentElement ?? canvas;

			const onMove = (e: PointerEvent) => {
				const rect = canvas.getBoundingClientRect();
				mouseX = e.clientX - rect.left;
				mouseY = e.clientY - rect.top;
				pointerOver = true;
				start();
			};
			const onLeave = () => {
				pointerOver = false;
				start();
			};

			draw();
			target.addEventListener('pointermove', onMove);
			target.addEventListener('pointerleave', onLeave);

			// Pause the render loop entirely while the icon is scrolled out of view.
			const io = new IntersectionObserver(
				([entry]) => {
					visible = !!entry?.isIntersecting;
					if (visible) {
						draw();
						if (pointerOver || springsActive()) start();
					}
				},
				{ threshold: 0 },
			);
			io.observe(canvas);

			cleanupAnimation = () => {
				cancelAnimationFrame(raf);
				running = false;
				io.disconnect();
				target.removeEventListener('pointermove', onMove);
				target.removeEventListener('pointerleave', onLeave);
			};
		};

		// The SVG markup is loaded from the provided image src URL. A raw SVG
		// string is intentionally not supported here.
		fetch(svg)
			.then((res) => res.text())
			.then(setup)
			.catch(() => {
				/* Ignore fetch failures — the icon simply does not render. */
			});

		return () => {
			cancelled = true;
			cleanupAnimation?.();
		};
	}, [svg, numericSize, color]);

	return (
		// Decorative icon canvas: it has no accessible name or content, so
		// screen readers ignore it. `aria-hidden` / `role` are intentionally
		// omitted — both are disallowed by a11y lint rules on <canvas>.
		<canvas
			ref={canvasRef}
			css={canvasCss}
			// width/height are derived from the runtime size, so they must stay
			// inline — they cannot be statically extracted into css({}).
			style={{ width: numericSize, height: numericSize }}
		/>
	);
};

export default UniformPixelIcon;
